/* ================= SERVICES ================= */
import React from "react";
import { FiServer, FiSettings, FiLayers, FiTrendingUp, FiGlobe, FiShield, FiZap, FiUsers } from "react-icons/fi";
import { SiJfrogpipelines } from "react-icons/si";
import {
  LuSettings,
  LuUsers,
  LuSearch,
  LuPalette,
  LuCircleCheck,
  LuSmartphone,
  LuShieldCheck,
  LuPercent,
  LuShield,
  LuCpu,
  LuClipboardList,
  LuCode,
  LuBug,
  LuRocket,
  LuActivity,
} from "react-icons/lu";

// 1. Hero Block Configuration
export const cloudHeroData = {
  backgroundImage: "/images/serviceBanners/DevopsBanner.webp",
  whiteImage: "/images/serviceBanners/DevopsBannerWhite.webp",
  subTitle: "Building Scalable DevOps & Cloud Solutions",
  title: (
    <>
      DevOps & <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-600">Cloud Engineering</span> Solutions
    </>
  ),
  description:
    "We build scalable DevOps and cloud engineering solutions that enhance deployment speed, system reliability, and infrastructure performance, enabling businesses to achieve efficient, secure, and high performing modern application delivery.",
  primaryButton: {
    title: "Get Started",
    onClick: () => console.log("Cloud Start clicked"),
  },
  secondaryButton: {
    title: "View Infrastructure",
    onClick: () => console.log("Cloud Work clicked"),
  },
  featureData: [
    {
      icon: "/images/services/website/high_performance.webp",
      title: "High Performance",
      desc: "Optimized cloud systems for speed and reliability.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/responsive.webp",
      title: "Responsive Design",
      desc: "Works smoothly across all devices and environments.",
      iconColor: "#22d3ee",
    },
    {
      icon: "/images/services/website/secure.webp",
      title: "Secure & Reliable",
      desc: "Strong security with stable cloud infrastructure.",
      iconColor: "#a855f7",
    },
    {
      icon: "/images/services/website/clean_code.webp",
      title: "Clean Code",
      desc: "Maintainable and developer-friendly system architecture.",
      iconColor: "#34d399",
    },
  ],
  floatingIcons: [
    {
      image: "/images/services/website/nodejs.webp",
      top: "16%",
      right: "6%",
      containerSize: 80,
      iconSize: 60,
      glowColor: "rgba(59,130,246,0.35)",
      iconGlow: "rgba(59,130,246,0.9)",
    },
  ],
};
// 2. Stats Block
export const cloudStatsData = [
  {
    icon: LuSettings,
    value: "150+",
    label: "Apps Built",
    colorClass: "text-[#00d2ff]",
    bgClass: "bg-[#00d2ff]/10",
    borderClass: "border-[#00d2ff]/20",
    glowClass: "shadow-[0_0_20px_rgba(0,210,255,0.15)]",
  },
  {
    icon: LuUsers,
    value: "90+",
    label: "Happy Clients",
    colorClass: "text-[#a855f7]",
    bgClass: "bg-[#a855f7]/10",
    borderClass: "border-[#a855f7]/20",
    glowClass: "shadow-[0_0_20px_rgba(168,85,247,0.15)]",
  },
  {
    icon: LuSmartphone,
    value: "50M+",
    label: "Downloads",
    colorClass: "text-[#06b6d4]",
    bgClass: "bg-[#06b6d4]/10",
    borderClass: "border-[#06b6d4]/20",
    glowClass: "shadow-[0_0_20px_rgba(6,182,212,0.15)]",
  },
  {
    icon: LuShieldCheck,
    value: "99.9%",
    label: "Crash-Free Apps",
    colorClass: "text-[#3b82f6]",
    bgClass: "bg-[#3b82f6]/10",
    borderClass: "border-[#3b82f6]/20",
    glowClass: "shadow-[0_0_20px_rgba(59,130,246,0.15)]",
  },
  {
    icon: LuPercent,
    value: "40%",
    label: "Faster Development",
    colorClass: "text-[#d946ef]",
    bgClass: "bg-[#d946ef]/10",
    borderClass: "border-[#d946ef]/20",
    glowClass: "shadow-[0_0_20px_rgba(217,70,239,0.15)]",
  },
  {
    icon: LuShield,
    value: "100%",
    label: "Client Satisfaction",
    colorClass: "text-[#6366f1]",
    bgClass: "bg-[#6366f1]/10",
    borderClass: "border-[#6366f1]/20",
    glowClass: "shadow-[0_0_20px_rgba(99,102,241,0.15)]",
  },
];

// 3. Solutions & Process Block (WhyChooseUs Data)
export const cloudSolutionsAndProcessData = {
  buildSubtitle: "OUR SERVICES",
  buildTitle: "Custom DevOps & Cloud Solutions for Every Business Need",
  buildCards: [
    {
      title: "Cross-Platform Infrastructure Support",
      desc: "Build flexible systems across cloud environments and platforms including AWS, Azure, and GCP.",
      icon: FiServer,
      colorClass: "text-[#00d2ff]",
      bgClass: "bg-[#00d2ff]/10",
      borderClass: "border-[#00d2ff]/20 hover:border-[#00d2ff]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(0,210,255,0.25)]",
    },
    {
      title: "Enterprise DevOps Solutions",
      desc: "Deliver secure and scalable infrastructure for large-scale operations with high availability.",
      icon: FiShield,
      colorClass: "text-[#3b82f6]",
      bgClass: "bg-[#3b82f6]/10",
      borderClass: "border-[#3b82f6]/20 hover:border-[#3b82f6]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(59,130,246,0.25)]",
    },
    {
      title: "E-Commerce Cloud Solutions",
      desc: "Support high-traffic stores with reliable cloud architecture and edge-optimized delivery.",
      icon: FiGlobe,
      colorClass: "text-[#a855f7]",
      bgClass: "bg-[#a855f7]/10",
      borderClass: "border-[#a855f7]/20 hover:border-[#a855f7]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(168,85,247,0.25)]",
    },
    {
      title: "On-Demand Cloud Systems",
      desc: "Enable real-time scaling for dynamic application workloads and auto-scaling logic.",
      icon: FiZap,
      colorClass: "text-[#06b6d4]",
      bgClass: "bg-[#06b6d4]/10",
      borderClass: "border-[#06b6d4]/20 hover:border-[#06b6d4]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(6,182,212,0.25)]",
    },
    {
      title: "FinTech Cloud Solutions",
      desc: "Ensure secure, compliant, and high-performance cloud systems with DevSecOps policies.",
      icon: FiShield,
      colorClass: "text-[#34d399]",
      bgClass: "bg-[#34d399]/10",
      borderClass: "border-[#34d399]/20 hover:border-[#34d399]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(52,211,153,0.25)]",
    },
    {
      title: "Custom DevOps Solutions",
      desc: "Tailored infrastructure and deployment pipelines based on your specific business goals.",
      icon: FiSettings,
      colorClass: "text-[#f59e0b]",
      bgClass: "bg-[#f59e0b]/10",
      borderClass: "border-[#f59e0b]/20 hover:border-[#f59e0b]/50",
      glowClass: "group-hover:shadow-[0_0_20px_rgba(245,158,11,0.25)]",
    },
  ],
  processSubtitle: "OUR METHODOLOGY",
  processTitle: "DevOps & Cloud Engineering Process",
  processSteps: [
    {
      step: "01",
      title: "Discovery & Consultation",
      description: "Understand business goals, system requirements, and cloud infrastructure needs.",
      icon: LuSearch,
    },
    {
      step: "02",
      title: "Strategy & Planning",
      description: "Define architecture, deployment strategy, and technology stack.",
      icon: LuClipboardList,
    },
    {
      step: "03",
      title: "System Design",
      description: "Design scalable, secure, and high-availability cloud infrastructure.",
      icon: LuPalette,
    },
    {
      step: "04",
      title: "Development & Automation",
      description: "Build CI/CD pipelines and automate deployment workflows.",
      icon: LuCode,
    },
    {
      step: "05",
      title: "Quality Testing",
      description: "Test performance, security, and system reliability.",
      icon: LuCircleCheck,
    },
    {
      step: "06",
      title: "Deployment & Support",
      description: "Deploy cloud systems and provide continuous monitoring and maintenance.",
      icon: LuRocket,
    },
  ],
  benefitsSubtitle: "WHY CHOOSE OUR DEVOPS & CLOUD ENGINEERING SERVICES?",
  benefitsTitle: "Why Businesses Choose DevOps & Cloud Engineering Solutions",
  benefits: [
    "Faster deployment pipelines",
    "Smarter cloud automation",
    "Modern system architecture",
    "High performance infrastructure",
    "Efficient DevOps workflow",
    "Scalable cloud solutions",
    "Cost-effective cloud operations",
  ],
  logoImage: "/images/serviceBanners/DevopsBanner.webp",
  whiteLogoImage: "/images/serviceBanners/DevopsBannerWhite.webp",
};

// 4. Portfolio Data
export const cloudPortfolioData = {
  subtitle: "CASE STUDIES",
  title: "Cloud Infrastructure That Scales",
  buttonText: "View Infrastructure Projects",
  buttonLink: "#",
  apps: [
    {
      title: "Global FinPort",
      badge: "AWS Infrastructure",
      badgeColor: "bg-[#00d2ff]/10 text-[#00d2ff] border-[#00d2ff]/20",
      desc: "Scaling a financial portal to handle 10k concurrent users with 99.99% uptime.",
      downloads: "Enterprise",
      rating: "5.0",
      image: "/images/serviceBanners/DevopsBanner.webp",
    },
  ],
};

// 5. Technologies Data
export const cloudTechnologiesData = {
  techTitle: "OUR CLOUD-NATIVE STACK",
  technologies: [
    { name: "AWS", icon: "Provider" },
    { name: "Kubernetes", icon: "RestAPI" },
    { name: "Docker", icon: "Vite" },
    { name: "Terraform", icon: "Code" },
    { name: "Jenkins", icon: "Firebase" },
    { name: "Prometheus", icon: "SQLite" },
  ],
  trustTitle: "TRUSTED BY TECHNOLOGY LEADERS Worldwide",
  trustClients: [
    { image: "/images/logo/google_logo.png", alt: "google" },
    { image: "/images/logo/microsoft_logo.png", alt: "microsoft" },
    { image: "/images/logo/amazon_logo.png", alt: "amazon" },
    { image: "/images/logo/Adobe_Corporate_logo.png", alt: "adobe" },
    { image: "/images/logo/IBM_logo.png", alt: "ibm" },
    { image: "/images/logo/Oracle_logo.png", alt: "oracle" },
  ],
};

// 6. About Overview
export const cloudAboutData = [
  {
    title: "DevOps Consulting Services for Faster and Reliable Operations",
    content: (
      <>
        Modern businesses require stable infrastructure that supports growth without performance disruptions. Reliable{" "}
        <b className="text-text-primary font-semibold">devops consulting services</b> help organizations improve system reliability, reduce operational bottlenecks, and maintain
        better collaboration between development and operations teams. Many companies struggle with delayed releases, infrastructure inefficiencies, and inconsistent environments
        that impact productivity. At <b className="text-text-primary font-semibold">Tycho Technologies</b>, DevOps strategies are designed around business objectives to help
        organizations improve agility, operational efficiency, and long-term scalability.
      </>
    ),
  },
  {
    title: "Cloud Application Development for Scalable Digital Growth",
    content: (
      <>
        Businesses adopting cloud technologies need systems that can adapt to changing workloads and business demands. Effective{" "}
        <b className="text-text-primary font-semibold">cloud application development</b> helps organizations improve flexibility, strengthen accessibility, and support digital
        operations without unnecessary infrastructure complexity. Companies investing in <b className="text-text-primary font-semibold">aws cloud migration services</b> often
        improve performance by modernizing outdated systems and improving infrastructure readiness for future growth. Tycho Technologies focuses on practical cloud solutions
        designed to support business continuity and stronger digital transformation outcomes.
      </>
    ),
  },
  {
    title: "DevOps Automation Solutions for Better Workflow Efficiency",
    content: (
      <>
        Managing infrastructure manually often slows development cycles and increases operational risk. Reliable{" "}
        <b className="text-text-primary font-semibold">devops automation solutions</b> help businesses simplify workflows, improve deployment consistency, and reduce operational
        friction across teams. Organizations looking to strengthen digital ecosystems often improve immersive technology performance by integrating infrastructure support with{" "}
        <a href="https://tychotechnologies.com/vr-app-development-services-india" className="text-cyan-400 underline hover:text-cyan-300">
          vr app development company
        </a>{" "}
        solutions for scalable application environments.
      </>
    ),
  },
  {
    title: "Why Businesses Choose a DevOps Company in India",
    content: (
      <>
        The demand for a trusted <b className="text-text-primary font-semibold">devops company in india</b> continues to grow as organizations seek scalable infrastructure and
        stronger operational performance. Businesses investing in <b className="text-text-primary font-semibold">cloud development services india</b> often prioritize system
        stability, automation, and long-term flexibility. Businesses partnering with Tycho Technologies gain access to customized DevOps frameworks designed around measurable
        operational goals. Many organizations also strengthen next-generation digital experiences through{" "}
        <a href="https://tychotechnologies.com/ar-app-development-services-india/" className="text-cyan-400 underline hover:text-cyan-300">
          ar app development company
        </a>{" "}
        solutions to support connected and scalable technology ecosystems.
      </>
    ),
  },
];

// 7. FAQs
export const cloudFaqs = [
  {
    q: "What is included in DevOps development services?",
    a: "Our services include CI/CD pipeline setup, cloud infrastructure management (IaC), automation, 24/7 monitoring, security integration, and ongoing platform support.",
  },
  {
    q: "Why should I choose DevOps development in India?",
    a: "India offers access to high-tier engineering talent and cost-effective operational models, ensuring your infrastructure is built to global best practices and compliance standards.",
  },
];
